import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import heroBg from "../../assets/hero1.webp";
import { apiRequest } from "../../utils/apiRequest";
import {
  getPortalSession,
  isPortalSessionExpired,
  clearPortalSession,
} from "../../utils/portalAuth";

export default function LoginPage() {
  const navigate = useNavigate();

  const [loginType, setLoginType] = useState("staff");
  const [formData, setFormData] = useState({
    username: "",
    staff_id: "",
    pin: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const session = getPortalSession();

    if (!session.token || !session.user) return;

    if (isPortalSessionExpired()) {
      clearPortalSession();
      return;
    }

    if (session.userType === "admin") {
      navigate("/admin", { replace: true });
    } else if (session.userType === "staff") {
      navigate("/staff", { replace: true });
    }
  }, [navigate]);

  const handleToggle = (type) => {
    setLoginType(type);
    setError("");
    setFormData({
      username: "",
      staff_id: "",
      pin: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "staff_id" ? value.replace(/\D/g, "") : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (loginType === "admin") {
      if (!formData.username.trim() || !formData.pin.trim()) {
        setError("Username and PIN are required.");
        return;
      }
    } else {
      if (!formData.staff_id.trim() || !formData.pin.trim()) {
        setError("Staff ID and PIN are required.");
        return;
      }
    }

    setLoading(true);

    const payload =
      loginType === "admin"
        ? {
            script: "admin-signin",
            data: {
              username: formData.username.trim(),
              pin: formData.pin.trim(),
            },
          }
        : {
            script: "staff-signin",
            data: {
              staff_id: `LAS-${formData.staff_id.trim()}`,
              pin: formData.pin.trim(),
            },
          };

    const res = await apiRequest(payload);

    setLoading(false);

    if (!res?.success) {
      setError(res?.error || "Login failed");
      return;
    }

    localStorage.setItem("staff_portal_token", res.api_token);
    localStorage.setItem("staff_portal_user_type", loginType);
    localStorage.setItem("staff_portal_expires_at", res.expires_at);

    if (loginType === "admin") {
      localStorage.setItem("staff_portal_user", JSON.stringify(res.admin));
      navigate("/admin", { replace: true });
    } else {
      localStorage.setItem("staff_portal_user", JSON.stringify(res.staff));
      navigate("/staff", { replace: true });
    }
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center relative flex items-center justify-center px-4"
      style={{
        backgroundImage: `url(${heroBg})`,
      }}
    >
      <div className="absolute inset-0 bg-black/60"></div>

      <div className="relative w-full max-w-md backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8 text-white">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold tracking-wide">
            Lasgidi Restaurant
          </h1>
          <p className="text-sm text-white/80 mt-1">Staff Portal</p>
        </div>

        <div className="flex bg-white/10 rounded-2xl p-1 mb-6 border border-white/20">
          <button
            type="button"
            onClick={() => handleToggle("staff")}
            className={`w-1/2 py-2 rounded-xl font-medium transition ${
              loginType === "staff"
                ? "bg-white text-black"
                : "text-white/80 hover:bg-white/10"
            }`}
          >
            Staff
          </button>

          <button
            type="button"
            onClick={() => handleToggle("admin")}
            className={`w-1/2 py-2 rounded-xl font-medium transition ${
              loginType === "admin"
                ? "bg-white text-black"
                : "text-white/80 hover:bg-white/10"
            }`}
          >
            Admin
          </button>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {loginType === "admin" ? (
            <div>
              <label className="block text-sm mb-1 text-white/80">
                Username
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Enter username"
                className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm mb-1 text-white/80">
                Staff ID
              </label>
              <div className="flex items-center rounded-xl bg-white/20 border border-white/30 overflow-hidden">
                <span className="px-3 text-white/70 text-sm">LAS-</span>
                <input
                  type="text"
                  name="staff_id"
                  value={formData.staff_id}
                  onChange={handleChange}
                  placeholder="***"
                  inputMode="numeric"
                  className="w-full px-3 py-3 bg-transparent outline-none placeholder-white/60"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm mb-1 text-white/80">PIN</label>
            <input
              type="password"
              name="pin"
              value={formData.pin}
              onChange={handleChange}
              placeholder={
                loginType === "staff" ? "Enter staff PIN" : "Enter admin PIN"
              }
              className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white"
            />
          </div>

          {error ? (
            <p className="text-sm text-red-300 bg-red-500/10 border border-red-400/30 rounded-xl px-3 py-2">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black font-semibold py-3 rounded-xl hover:bg-gray-200 transition duration-200 disabled:opacity-70"
          >
            {loading
              ? "Signing In..."
              : loginType === "staff"
                ? "Sign in as Staff"
                : "Sign in as Admin"}
          </button>
        </form>

        <p className="text-center text-xs text-white/60 mt-6">
          © {new Date().getFullYear()} Lasgidi Restaurant
        </p>
      </div>
    </div>
  );
}